import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatINRFull, formatDate, apiErrorMsg } from '@/lib/formatters';
import { Plus, CheckCircle, XCircle, IndianRupee } from 'lucide-react';

const STATUS_COLORS = {
  submitted: 'bg-blue-100 text-blue-800',
  verified: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  refunded: 'bg-amber-100 text-amber-800',
};

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchPayments = () => {
    setLoading(true);
    api.get('/payments').then(r => { setPayments(r.data.payments); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchPayments(); }, []);

  const handleVerify = async (paymentId, status) => {
    try {
      await api.put(`/payments/${paymentId}`, { status });
      toast.success(`Payment ${status}`);
      fetchPayments();
    } catch (err) { toast.error(apiErrorMsg(err)); }
  };

  const totalVerified = payments.filter(p => p.status === 'verified').reduce((s, p) => s + (p.amount || 0), 0);
  const totalPending = payments.filter(p => p.status === 'submitted').reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Payments & Collections</h1>
          <p className="text-slate-500 text-sm mt-0.5">{payments.length} payment records</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="record-payment-btn">
          <Plus size={16} /> Record Payment
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="border-slate-200 kpi-card">
          <CardContent className="p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Verified Collections</p>
            <p className="text-2xl font-extrabold font-mono text-emerald-700">{formatINRFull(totalVerified)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 kpi-card">
          <CardContent className="p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Pending Verification</p>
            <p className="text-2xl font-extrabold font-mono text-amber-600">{formatINRFull(totalPending)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 kpi-card">
          <CardContent className="p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Total Records</p>
            <p className="text-2xl font-extrabold font-mono">{payments.length}</p>
          </CardContent>
        </Card>
      </div>

      <div className="table-container bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Customer</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Project</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Amount</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Mode</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">UTR</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Type</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Date</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 9 }).map((_, j) => <TableCell key={j}><div className="h-4 bg-slate-100 rounded animate-pulse" /></TableCell>)}</TableRow>
              ))
            ) : payments.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-400">No payments recorded yet</TableCell></TableRow>
            ) : (
              payments.map(p => (
                <TableRow key={p._id} data-testid={`payment-row-${p._id}`}>
                  <TableCell className="text-sm font-medium">{p.lead_name}</TableCell>
                  <TableCell className="text-sm">{p.project_name}</TableCell>
                  <TableCell className="text-sm font-semibold">{formatINRFull(p.amount)}</TableCell>
                  <TableCell className="text-sm">{p.mode}</TableCell>
                  <TableCell className="text-xs font-mono text-slate-500">{p.utr || '-'}</TableCell>
                  <TableCell className="text-xs">{p.type}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[p.status] || 'bg-slate-100'}`}>
                      {p.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{formatDate(p.date)}</TableCell>
                  <TableCell>
                    {p.status === 'submitted' && (
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="h-7 text-emerald-600" onClick={() => handleVerify(p._id, 'verified')} data-testid={`verify-payment-${p._id}`}>
                          <CheckCircle size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => handleVerify(p._id, 'rejected')} data-testid={`reject-payment-${p._id}`}>
                          <XCircle size={14} />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {showCreate && <RecordPaymentDialog open onClose={() => setShowCreate(false)} onCreated={() => { fetchPayments(); setShowCreate(false); }} />}
    </div>
  );
}

function RecordPaymentDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ booking_id: '', lead_name: '', project_name: '', amount: '', mode: 'NEFT', utr: '', date: '', type: 'installment', remarks: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/payments', { ...form, amount: Number(form.amount) || 0 });
      toast.success('Payment recorded');
      onCreated();
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Booking/Lead ID</Label><Input value={form.booking_id} onChange={e => setForm({ ...form, booking_id: e.target.value })} data-testid="payment-booking-id" /></div>
            <div><Label>Customer Name</Label><Input value={form.lead_name} onChange={e => setForm({ ...form, lead_name: e.target.value })} /></div>
            <div><Label>Project</Label><Input value={form.project_name} onChange={e => setForm({ ...form, project_name: e.target.value })} /></div>
            <div><Label>Amount *</Label><Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required data-testid="payment-amount" /></div>
            <div>
              <Label>Mode</Label>
              <Select value={form.mode} onValueChange={v => setForm({ ...form, mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NEFT">NEFT</SelectItem>
                  <SelectItem value="RTGS">RTGS</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>UTR / Ref</Label><Input value={form.utr} onChange={e => setForm({ ...form, utr: e.target.value })} /></div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="token">Token</SelectItem>
                  <SelectItem value="installment">Installment</SelectItem>
                  <SelectItem value="final">Final Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-payment-btn">{saving ? 'Recording...' : 'Record Payment'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
